import React, { PropsWithChildren, ReactNode, useCallback, useMemo, useState } from 'react';
import { Button } from '@/comps/primitives/button';
import { FlaskConical, ListChecks, Plus, X } from 'lucide-react';
import { WithClassName } from '@/types/component';
import {
  CustomAggregateCategoryDialogForm,
  CustomAggregateCategorySubmitParams,
} from '@/domains/user-intelligence/groupby/components/customEntitties/CustomAggregateCategoryDialogForm';
import {
  QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES,
  QUERY_KEY_INTERACTION_AGGREGATES,
  QUERY_KEY_USER_USER_INTELLIGENCE_VERTICAL_TABLE__RISKY_BEHAVIORS,
} from '@/constants/query-keys';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogClose, DialogContentWrapper, DialogTitle, DialogTrigger } from '@/comps/primitives/dialog';
import { NavElement } from '@/domains/home/components/home-tabs/_components/nav-button';
import { cn } from '@/utils/shad';
import { capitalize } from 'lodash';
import { useProjectsContext } from '@/providers/projects-provider';
import { useJobToast } from '@/components/async-job-toast';
import { CreateCategoryPayloadFilters } from '@/api/generated/model';
import { useCreateCategory } from '@/api/generated/categories/categories';
import { BasicTooltip } from '@/comps/basic-tooltip';
import { useAliasedKindName, type KindAliasFn } from '@/contexts/alias';
import { translate } from '@/utils/translations';

type Props = WithClassName &
  PropsWithChildren & {
    kind: string;
    projectId: string;
    onSuccess?: () => void;
    onCancel?: () => void;
    name?: string;
    selectedItems?: string[];
    groupBy: 'action' | 'keyword' | 'interaction' | 'user_query' | 'conversation';
  };


function aggregateKindLabel(kind: string, aliasKindName: KindAliasFn): string {
  if (kind === 'model_suggestion') {
    return aliasKindName('type_of_problem_v2') ?? translate('filter', 'type_of_problem_v2');
  }
  if (kind === 'action') {
    return aliasKindName('user_action') ?? translate('filter', 'user_action');
  }
  if (kind === 'topic') {
    return aliasKindName('topic') ?? translate('filter', 'topic');
  }
  return 'custom category';
}

function aggregateCategoryDialogTitle(mode: 'create' | 'edit', kind: string, aliasKindName: KindAliasFn): string {
  if (kind !== 'topic' && kind !== 'action' && kind !== 'model_suggestion') {
    return mode === 'edit' ? 'Edit custom category' : 'Create Custom Category';
  }
  const label = aggregateKindLabel(kind, aliasKindName);
  if (kind === 'model_suggestion') {
    return mode === 'edit' ? `Edit ${label}` : 'Create Problem';
  }
  return mode === 'edit' ? `Edit ${label}` : `Create ${label}`;
}

type AggregateCategoryDialogProps = PropsWithChildren &
  Props & {
    renderContent?: (setOpen: (open: boolean) => void) => ReactNode;
    mode: 'create' | 'edit';
    groupBy: 'action' | 'keyword' | 'user_query' | 'interaction' | 'conversation';
  };

export function AggregateCategoryDialog({
  kind,
  projectId,
  onSuccess,
  name,
  selectedItems = [],
  children,
  renderContent,
  mode,
  groupBy,
}: AggregateCategoryDialogProps) {
  const [open, setOpen] = useState(false);

  const aliasKindName = useAliasedKindName();
  const title = useMemo(
    () => aggregateCategoryDialogTitle(mode, kind, aliasKindName),
    [aliasKindName, kind, mode],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen} modal>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContentWrapper className="gap-0 p-0">
        <DialogTitle>
          <div className="flex items-center justify-between px-7">
            <NavElement Icon={FlaskConical} text={title} coloredIcon isActive showTooltip={false} />

            <DialogClose>
              <X size={20} className="text-muted-foreground hover:cursor-pointer" />
            </DialogClose>
          </div>
        </DialogTitle>

        {renderContent ? (
          renderContent(setOpen)
        ) : (
          <CustomCategoryCreationDialogContent
            kind={kind}
            projectId={projectId}
            onSuccess={() => {
              onSuccess?.();
              setOpen(false);
            }}
            name={name}
            selectedItems={selectedItems}
            groupBy={groupBy}
          />
        )}
      </DialogContentWrapper>
    </Dialog>
  );
}

type DialogContentProps = {
  parentKind?: string;
  parentName?: string;
  kind?: string;
  projectId: string;
  onSuccess?: () => void;
  onSubmit?: () => void;
  name?: string;
  cancelButton?: ReactNode;

  // TODO: implement this properly when BE supports it
  selectedItems?: string[];
  groupBy?: 'action' | 'keyword' | 'user_query' | 'interaction' | 'conversation';
};

export function CustomCategoryCreationDialogContent({
  kind,
  projectId,
  onSuccess,
  onSubmit,
  selectedItems,
  name,
  groupBy,
  cancelButton,
}: DialogContentProps) {
  const { spawnToast } = useJobToast();

  const { mutateAsync: createCustomAggregateCategories } = useCreateCategory({
    mutation: {
      onError: (error) => {
        console.error('Error creating custom category:', error);
      },
    },
  });
  const queryClient = useQueryClient();

  const handleSubmit = useCallback(
    async (params: CustomAggregateCategorySubmitParams) => {
      const { parent, name, description } = params;
      if (!kind) {
        console.error('No kind specified for the custom category');
        return;
      }

      onSubmit?.();

      const createFiltersFromGroupBy = (): CreateCategoryPayloadFilters | undefined => {
        if (!selectedItems || selectedItems.length === 0) return undefined;
        switch (groupBy) {
          case 'action':
            return undefined;
          case 'keyword':
            return { type: 'keyword', values: selectedItems };
          case 'user_query':
            return { type: 'query', values: selectedItems };
          case 'interaction':
            return { type: 'id', values: selectedItems };
          case 'conversation':
            return { type: 'conversation_id', values: selectedItems };
          default:
            return undefined;
        }
      };

      const job_id = await createCustomAggregateCategories({
        data: {
          name: kind,
          parent_category_name: parent ? parent : undefined,
          description: description,
          project_id: projectId,
          value: name.toLowerCase(),
          filters: createFiltersFromGroupBy(),
        },
      });

      if (job_id) {
        spawnToast(job_id, `Creating ${kind} category`, () => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_INTERACTION_AGGREGATES] });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_USER_USER_INTELLIGENCE_VERTICAL_TABLE__RISKY_BEHAVIORS],
          });
          onSuccess?.();
        });
      }
    },
    [
      spawnToast,
      createCustomAggregateCategories,
      kind,
      onSubmit,
      onSuccess,
      projectId,
      queryClient,
      selectedItems,
      groupBy,
    ],
  );

  return (
    <>
      {selectedItems && selectedItems.length > 0 && (
        <CreateCategoryDialogSection
          title={
            <p>
              Selected items{' '}
              <span className="text-xs text-muted-foreground underline decoration-neutral-600 underline-offset-2">
                (+{selectedItems.length})
              </span>
            </p>
          }
          icon={<ListChecks className="h-4 w-4" />}
        >
          <ul className="max-h-25 list-disc overflow-y-auto pl-5">
            {selectedItems.map((item) => (
              <li key={item} className="text-sm text-muted-foreground">
                {capitalize(item)}
              </li>
            ))}
          </ul>
        </CreateCategoryDialogSection>
      )}
      <CustomAggregateCategoryDialogForm
        kind={kind}
        onSubmit={handleSubmit}
        name={name}
        selectedItems={selectedItems}
        mode="create"
        cancelButton={cancelButton}
      />
    </>
  );
}

export function CreateAggregateCategoryPortalButton({
  items,
  kind,
  projectId,
  customCategoryName,
  className,
  groupBy,
}: {
  items: string[];
  kind: string;
  projectId: string;
  customCategoryName?: string;
  groupBy: 'action' | 'keyword' | 'user_query' | 'interaction' | 'conversation';
} & WithClassName) {
  const aliasKindName = useAliasedKindName();
  const buttonLabel = useMemo(
    () => aggregateCategoryDialogTitle('create', kind, aliasKindName),
    [aliasKindName, kind],
  );

  const { isMappedProject } = useProjectsContext();

  return (
    <AggregateCategoryDialog
      kind={kind}
      projectId={projectId}
      selectedItems={items}
      name={customCategoryName}
      mode="create"
      groupBy={groupBy}
    >
      <Button
        size="sm"
        className={cn(
          'flex items-center rounded-base border border-accent-foreground text-[12px] font-semibold hover:bg-transparent',
          className,
        )}
        variant="ghost"
        disabled={isMappedProject}
        disabledMessage="Creating new aggregates in custom projects is currently not supported."
      >
        <Plus size={12} />
        <BasicTooltip
          text={
            <div className="flex flex-col items-start gap-1">
              <p>Selected items:</p>
              <ul className="list-disc pl-4">
                {items.map((item) => (
                  <li key={item} className="text-xs text-muted-foreground">
                    {capitalize(item)}
                  </li>
                ))}
              </ul>
            </div>
          }
        >
          <span>{buttonLabel}</span>
        </BasicTooltip>
      </Button>
    </AggregateCategoryDialog>
  );
}

export function CreateCategoryDialogSection({
  title,
  className,
  icon,
  children,
  classNames,
}: PropsWithChildren & {
  className?: string;
  icon?: ReactNode;
  title?: ReactNode;
  classNames?: {
    title?: {
      p?: string;
    };
    children?: {
      div?: string;
    };
  };
}) {
  return (
    <section className={cn('border-t', className)}>
      {title && (
        <header className="flex h-12 items-center gap-1 border-b px-8 text-muted-foreground">
          {icon}
          {title && <p className={cn(classNames?.title?.p)}>{title}</p>}
        </header>
      )}
      {children && <div className={cn('px-8 py-3', classNames?.children?.div)}>{children}</div>}
    </section>
  );
}
